// Utility functions for meetings queries

import { STATUS_MAP } from '../../../services/search_api/constants'

interface QueryVariables {
  first?: number
  after?: string
  before?: string
  role?: string[]
  scope_type?: string | null
  scopeType?: string | null
  scope_id?: string | null
  scopeId?: string | null
  start_time?: any
  startTime?: any
  kinds?: string[]
  status?: string
  statuses?: string[]
  statusIds?: string[]
  is_organizer?: boolean
  isOrganizer?: boolean
  include_notetaker_ineligible_external_events?: boolean
  includeNotetakerIneligibleExternalEvents?: boolean
  ids?: string[]
  uuid?: string
  orderBy?: any
  direction?: string
}

const GRAPHQL_ERRORS_PROP = '_graphqlErrors'

/**
 * Maps frontend scope type values (e.g. from route) to backend ScopeType enum values.
 * Used so GraphQL receives valid enum values and we avoid sending scopeId "me" as an ID.
 *
 * @param scopeType - The scope type from the route (e.g., 'user', 'team', 'all_users_and_teams')
 * @param scopeId - Optional scope ID from the route (e.g., user ID, team ID, or 'me')
 * @returns The backend scope type value, or null if the scope type is unknown
 */
export const mapScopeTypeToBackendValue = (
  scopeType: string | null | undefined,
  scopeId?: string | null
): string | null => {
  switch (scopeType) {
    case 'user': {
      if (scopeId === 'me') {
        return 'my_calendly'
      }
      if (scopeId) {
        return 'user_single'
      }
      return 'all_users'
    }
    case 'team': {
      if (scopeId) {
        return 'page_single'
      }
      return 'page'
    }
    case 'all_users_and_teams':
      return 'all_users_and_teams'
    case 'all_users':
      return 'all_users'
    case 'user_group':
      return 'user_group'
    default:
      return null
  }
}

/**
 * Transforms query variables from snake_case to camelCase for GraphQL query consistency.
 * Also handles legacy uuid (singular) to ids (array) conversion.
 * Supports both status (single string, legacy) and statuses (array, preferred).
 */
export const transformQueryVariables = (variables: QueryVariables): Record<string, any> => {
  const transformedVariables: any = {
    first: variables.first,
    after: variables.after,
    before: variables.before,
    role: variables.role,
    scopeType: variables.scope_type ?? variables.scopeType,
    scopeId: variables.scope_id ?? variables.scopeId,
    startTime: variables.start_time ?? variables.startTime,
    kinds: variables.kinds,
    isOrganizer: variables.is_organizer ?? variables.isOrganizer,
    includeNotetakerIneligibleExternalEvents:
      variables.include_notetaker_ineligible_external_events
      ?? variables.includeNotetakerIneligibleExternalEvents,
    // Support both ids (array, preferred) and uuid (singular, legacy compatibility)
    // ids is preferred as it matches the GraphQL schema directly
    ids: variables.ids || (variables.uuid ? [variables.uuid] : undefined),
    orderBy: variables.orderBy,
  }

  // Status handling: prefer statuses (array) over status (single string)
  // Priority: statusIds > statuses > status
  // Web uses statusIds/statuses (arrays), legacy/mobile uses status (single string)
  if (variables.statusIds) {
    // Frontend sends statusIds as array with lowercase values - transform to uppercase GraphQL enum values
    // e.g., 'active' -> 'DEFAULT', 'canceled' -> 'CANCELED'
    transformedVariables.statuses = variables.statusIds
      .map((s) => STATUS_MAP[s])
      .filter(Boolean)
  } else if (variables.statuses) {
    transformedVariables.statuses = variables.statuses
  } else if (variables.status) {
    // Legacy: single string for backward compatibility (e.g., mobile)
    transformedVariables.status = STATUS_MAP[variables.status] || variables.status
  }

  // Remove undefined values
  Object.keys(transformedVariables).forEach((key) => {
    if (transformedVariables[key] === undefined) {
      delete transformedVariables[key]
    }
  })

  return transformedVariables
}

/**
 * Filters out null edges/nodes from GraphQL response to handle backend data issues gracefully.
 */
export const filterNullEdges = (response: any) => {
  if (!response?.meetings?.edges) {
    return response
  }
  return {
    ...response,
    meetings: {
      ...response.meetings,
      edges: response.meetings.edges.filter((edge: any) => edge?.node != null),
    },
  }
}

/**
 * Filters out edges with GraphQL errors and null edges from response.
 * Also handles GraphQL partial responses (errors + data).
 */
export const filterErrorEdges = (response: any) => {
  if (!response?.meetings?.edges) {
    return response
  }

  // Check if response has GraphQL errors attached (from base query handling)
  // GraphQL errors can indicate which edges failed (e.g., ["meetings", "edges", 10, "node", "owner_user_uuid"])
  const errorEdgeIndices = new Set<number>()
  const errorDetails: Array<{ index: number; error: any }> = []
  if (response[GRAPHQL_ERRORS_PROP] && Array.isArray(response[GRAPHQL_ERRORS_PROP])) {
    response[GRAPHQL_ERRORS_PROP].forEach((error: any) => {
      if (error.path && Array.isArray(error.path)) {
        // Find edge index in path (e.g., ["meetings", "edges", 10, "node", ...])
        const edgesIndex = error.path.indexOf('edges')
        if (edgesIndex !== -1 && edgesIndex + 1 < error.path.length) {
          const edgeIndex = error.path[edgesIndex + 1]
          if (typeof edgeIndex === 'number') {
            errorEdgeIndices.add(edgeIndex)
            errorDetails.push({ index: edgeIndex, error })
          }
        }
      }
    })
  }

  // Filter out null edges and edges with errors
  const filteredEdges = response.meetings.edges.filter((edge: any, index: number) => {
    // Filter out null edges or edges with null nodes
    if (edge == null || edge.node == null) {
      return false
    }
    // Filter out edges that have errors
    if (errorEdgeIndices.has(index)) {
      return false
    }
    return true
  })

  // Log warning for filtered error edges
  if (errorDetails.length > 0) {
    console.warn(
      `[MeetingsRealtime] Filtered out ${errorDetails.length} edge(s) due to GraphQL errors:`,
      errorDetails.map(({ index, error }) => ({
        edgeIndex: index,
        path: error.path,
        message: error.message,
        code: error.extensions?.code,
      }))
    )
  }

  // Remove the temporary _graphqlErrors property before returning
  const { [GRAPHQL_ERRORS_PROP]: _, ...cleanResponse } = response

  return {
    ...cleanResponse,
    meetings: {
      ...cleanResponse.meetings,
      edges: filteredEdges,
    },
  }
}

const BUFFER_KEYS = {
  BEFORE: 'before_buffer_time',
  AFTER: 'after_buffer_time',
}

/**
 * Returns the key for the buffer time based on the isBefore flag.
 * @param isBefore - Whether the buffer is before or after the event.
 * @returns The key for the buffer time.
 */
export const getBufferKey = (isBefore: boolean) => (
  isBefore ? BUFFER_KEYS.BEFORE : BUFFER_KEYS.AFTER
)
